import React, { useEffect, useState, useCallback } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  KeyRound,
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Clock3,
  Activity,
  ShieldCheck,
  BookOpen,
} from "lucide-react";

/**
 * v2 PR-2B — Settings → API Tokens.
 *
 * Lists active + revoked tokens for the caller's org, lets an org_admin
 * mint a new one (showing the plaintext exactly once), and revoke at will.
 * The plaintext "I've saved this" modal is the only place the token is
 * ever visible — there's no view-key action anywhere else.
 */
export default function SettingsApiTokens() {
  const [tokens, setTokens] = useState([]);
  const [scopesMeta, setScopesMeta] = useState({ available: [], grouped: {} });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [shownToken, setShownToken] = useState(null); // {plaintext, name, scopes, prefix}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: meta }] = await Promise.all([
        api.get("/api-tokens"),
        api.get("/api-tokens/_meta/scopes"),
      ]);
      setTokens(list || []);
      setScopesMeta(meta || { available: [], grouped: {} });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = (resp) => {
    setShownToken(resp);
    setCreateOpen(false);
    load();
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"?\n\nThe token will stop working immediately. This cannot be undone.`)) return;
    try {
      await api.delete(`/api-tokens/${t.id}`);
      toast.success(`Revoked "${t.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="api-tokens-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eb-overline mb-1">v2 PR-2B · Public API</div>
            <h1 className="text-3xl font-bold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <KeyRound className="w-7 h-7 text-orange-500" /> API Tokens
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-2 max-w-3xl">
              Scoped Bearer tokens for third-party integrations against the public <code className="px-1 rounded bg-navy-50 dark:bg-navy-700 dark:text-navy-100">/api/v1/*</code> endpoints.
              Plaintext is shown <strong>once</strong> at creation — store it in your integration's secret vault immediately.
              Rate-limited to 600 requests/minute per token. <a className="text-orange-600 hover:underline inline-flex items-center gap-1" href="/api-docs" target="_blank" rel="noreferrer"><BookOpen className="w-3 h-3" /> Open API docs</a>
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="api-tokens-create-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create new token
          </Button>
        </div>

        {loading ? (
          <div className="text-navy-400">Loading…</div>
        ) : tokens.length === 0 ? (
          <EmptyState onCreate={() => setCreateOpen(true)} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="api-tokens-list">
            {tokens.map((t) => (
              <TokenCard key={t.id} t={t} onRevoke={() => revoke(t)} />
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <CreateTokenDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          scopesMeta={scopesMeta}
          onCreated={onCreated}
        />
      )}

      {shownToken && (
        <ShowOnceDialog
          token={shownToken}
          onClose={() => setShownToken(null)}
        />
      )}
    </SettingsLayout>
  );
}

// -----------------------------------------------------------------------

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-800 p-10 text-center"
         data-testid="api-tokens-empty-state">
      <KeyRound className="w-10 h-10 text-orange-400 mx-auto mb-3" />
      <h2 className="text-lg font-semibold text-navy-900 dark:text-white mb-1">No tokens yet</h2>
      <p className="text-sm text-navy-500 dark:text-navy-300 max-w-md mx-auto mb-4">
        Mint your first API token to integrate EntryBook with your existing systems — visitor lookups,
        automated pre-registrations, custom dashboards.
      </p>
      <Button onClick={onCreate} className="bg-orange-500 hover:bg-orange-600 text-white">
        <Plus className="w-4 h-4 mr-1.5" /> Create your first token
      </Button>
    </div>
  );
}

function TokenCard({ t, onRevoke }) {
  const revoked = !!t.revoked_at;
  const expired = !revoked && t.expires_at && t.expires_at < new Date().toISOString();
  const stateColour = revoked
    ? "bg-danger-50 border-danger-200 text-danger-700"
    : expired
      ? "bg-warning-50 border-warning-200 text-warning-700"
      : "bg-success-50 border-success-200 text-success-700";
  const stateLabel = revoked ? "Revoked" : expired ? "Expired" : "Active";
  const StateIcon = revoked ? Trash2 : expired ? Clock3 : ShieldCheck;
  return (
    <div className="rounded-2xl bg-white dark:bg-navy-800 border-2 border-navy-100 dark:border-navy-700 p-5"
         data-testid={`api-token-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-navy-900 dark:text-white">{t.name}</h3>
          <p className="text-xs text-navy-500 dark:text-navy-300 mt-0.5">
            Prefix <code className="px-1 rounded bg-navy-50 dark:bg-navy-700 dark:text-navy-100">eb_live_{t.prefix}…</code>
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${stateColour}`}
              data-testid={`api-token-state-${t.id}`}>
          <StateIcon className="w-3 h-3" /> {stateLabel}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`api-token-scopes-${t.id}`}>
        {(t.scopes || []).map((s) => (
          <span key={s} className="text-[11px] font-mono px-2 py-0.5 rounded bg-navy-50 dark:bg-navy-700 text-navy-700 dark:text-navy-100 border border-navy-100 dark:border-navy-600">
            {s}
          </span>
        ))}
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="eb-overline mb-1">Expires</dt>
          <dd className="text-navy-700 dark:text-navy-200">{relative(t.expires_at) || "—"}</dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">Last used</dt>
          <dd className="text-navy-700 dark:text-navy-200 inline-flex items-center gap-1">
            <Activity className="w-3 h-3 opacity-50" />{relative(t.last_used_at) || "never"}
          </dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">Usage</dt>
          <dd className="text-navy-700 dark:text-navy-200">{t.usage_count || 0} calls</dd>
        </div>
        <div>
          <dt className="eb-overline mb-1">From subnet</dt>
          <dd className="text-navy-700 dark:text-navy-200 font-mono">{t.last_used_ip_subnet || "—"}</dd>
        </div>
      </dl>

      {!revoked && !expired && (
        <div className="mt-4 flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={onRevoke}
            className="text-danger-600 hover:bg-danger-50"
            data-testid={`api-token-revoke-${t.id}`}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Revoke
          </Button>
        </div>
      )}
    </div>
  );
}

function relative(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (d.getTime() - Date.now()) / 1000;
    const abs = Math.abs(diff);
    const past = diff < 0;
    const fmt = (n, u) => `${Math.floor(n)}${u} ${past ? "ago" : "from now"}`;
    if (abs < 60) return `just now`;
    if (abs < 3600) return fmt(abs / 60, "m");
    if (abs < 86400) return fmt(abs / 3600, "h");
    if (abs < 86400 * 30) return fmt(abs / 86400, "d");
    if (abs < 86400 * 365) return fmt(abs / (86400 * 30), "mo");
    return fmt(abs / (86400 * 365), "y");
  } catch { return iso; }
}

// -----------------------------------------------------------------------

function CreateTokenDialog({ open, onClose, scopesMeta, onCreated }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState(defaultExpiry());
  const [working, setWorking] = useState(false);

  const toggleScope = (s) => {
    setScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (scopes.length === 0) { toast.error("Select at least one scope"); return; }
    setWorking(true);
    try {
      const payload = { name: name.trim(), scopes };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      const { data } = await api.post("/api-tokens", payload);
      onCreated(data);
      toast.success("Token created — copy it now");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "expires_at_too_far") {
        toast.error("Lifetime capped at 2 years — pick an earlier date");
      } else if (det?.error === "unknown_scope") {
        toast.error(`Unknown scope: ${det.scope}`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="api-tokens-create-dialog">
        <DialogHeader>
          <DialogTitle>Create API token</DialogTitle>
          <DialogDescription>
            Pick the scopes your integration actually needs — the principle of least privilege keeps you safe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="api-token-name">Name</Label>
            <Input
              id="api-token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme integration"
              maxLength={60}
              data-testid="api-token-name-input"
            />
            <p className="mt-1 text-[11px] text-navy-400">Anything that helps you remember which system this token belongs to.</p>
          </div>

          <div>
            <Label>Scopes</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="api-token-scopes-grid">
              {Object.entries(scopesMeta.grouped || {}).map(([group, items]) => (
                <div key={group} className="rounded-lg border border-navy-100 dark:border-navy-700 p-3">
                  <div className="text-xs font-semibold text-navy-700 dark:text-navy-200 mb-2">{group}</div>
                  <div className="space-y-1.5">
                    {items.map((s) => (
                      <label key={s} className="flex items-center gap-2 text-xs text-navy-700 dark:text-navy-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={scopes.includes(s)}
                          onChange={() => toggleScope(s)}
                          className="rounded"
                          data-testid={`api-token-scope-${s.replace(":", "-")}`}
                        />
                        <code className="text-[11px]">{s}</code>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="api-token-expires">Expires</Label>
            <Input
              id="api-token-expires"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="api-token-expires-input"
            />
            <p className="mt-1 text-[11px] text-navy-400">Default: 1 year from today. Maximum: 2 years.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={working || !name.trim() || scopes.length === 0}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="api-token-submit-btn"
          >
            {working ? "Creating…" : (<><KeyRound className="w-4 h-4 mr-1" /> Create token</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultExpiry() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  d.setSeconds(0); d.setMilliseconds(0);
  // datetime-local format: YYYY-MM-DDTHH:MM
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// -----------------------------------------------------------------------

function ShowOnceDialog({ token, onClose }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the token manually");
    }
  };

  return (
    <Dialog open={!!token} onOpenChange={(o) => { if (!o && acknowledged) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="api-tokens-show-once-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-600" />
            Token created — copy it now
          </DialogTitle>
          <DialogDescription>
            <strong>{token.name}</strong> · scopes: {(token.scopes || []).join(", ")}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-warning-300 bg-warning-50 p-3 text-xs text-warning-800 inline-flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            This is the <strong>only</strong> time you'll see this token. Store it in your integration's secret vault now.
            EntryBook will never display it again — if it leaks, revoke and re-mint.
          </span>
        </div>

        <div className="rounded-lg bg-navy-900 text-navy-50 font-mono text-xs p-3 break-all"
             data-testid="api-token-plaintext">
          {token.token}
        </div>

        <div className="flex gap-2">
          <Button onClick={copy} className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="api-token-copy-btn">
            <Copy className="w-4 h-4 mr-1" /> {copied ? "Copied" : "Copy to clipboard"}
          </Button>
        </div>

        <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-navy-200 mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="api-token-acknowledge-checkbox"
          />
          I've saved this token in my secret store.
        </label>

        <DialogFooter>
          <Button
            disabled={!acknowledged}
            onClick={onClose}
            data-testid="api-token-done-btn"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
